import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Clock, User, Trash2, Bell, ChevronLeft, Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PatientWaitlist() {
  const { api } = useAuth();
  const [waitlist, setWaitlist] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newEntry, setNewEntry] = useState({
    specialty_id: '',
    accept_any_doctor: false,
    accept_any_clinic: false,
    notify_if_today: true,
    notify_if_earlier: true,
  });

  const fetchData = useCallback(async () => {
    try {
      const [waitlistRes, specialtiesRes] = await Promise.all([
        api.get('/patient/waitlist'),
        axios.get(`${API_URL}/api/public/specialties`)
      ]);
      setWaitlist(waitlistRes.data);
      setSpecialties(specialtiesRes.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateEntry = async () => {
    if (!newEntry.specialty_id) {
      toast.error('Selecione uma especialidade');
      return;
    }

    try {
      await api.post('/patient/waitlist', newEntry);
      toast.success('Adicionado à lista de espera');
      setDialogOpen(false);
      setNewEntry({
        specialty_id: '',
        accept_any_doctor: false,
        accept_any_clinic: false,
        notify_if_today: true,
        notify_if_earlier: true,
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar à lista');
    }
  };

  const handleDeleteEntry = async (id) => {
    try {
      await api.delete(`/patient/waitlist/${id}`);
      toast.success('Removido da lista de espera');
      fetchData();
    } catch (error) {
      toast.error('Erro ao remover da lista');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      active: { label: 'Ativa', className: 'bg-emerald-100 text-emerald-700' },
      notified: { label: 'Notificado', className: 'bg-blue-100 text-blue-700' },
      booked: { label: 'Agendado', className: 'bg-slate-100 text-slate-700' },
      expired: { label: 'Expirado', className: 'bg-slate-100 text-slate-500' },
      cancelled: { label: 'Cancelado', className: 'bg-red-100 text-red-700' },
    };
    const { label, className } = statusMap[status] || statusMap.active;
    return <Badge className={className}>{label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="patient-waitlist">
      <Navbar />

      <div className="container-app py-8">
        <Link to="/paciente" className="inline-flex items-center gap-2 text-slate-600 hover:text-primary mb-6">
          <ChevronLeft className="w-4 h-4" />
          Voltar
        </Link>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Lista de Espera</h1>
            <p className="text-slate-600 mt-1">Receba alertas quando horários ficarem disponíveis</p>
          </div>
          <Button onClick={() => setDialogOpen(true)} className="bg-primary" data-testid="add-waitlist-btn">
            <Plus className="w-4 h-4 mr-2" />
            Adicionar
          </Button>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse h-32 bg-white rounded-2xl"></div>
            ))}
          </div>
        ) : waitlist.length > 0 ? (
          <div className="space-y-4">
            {waitlist.map((entry) => (
              <div key={entry.id} className="bg-white rounded-2xl border border-slate-100 p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                      <Bell className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        {getStatusBadge(entry.status)}
                      </div>
                      {entry.doctor_name ? (
                        <h3 className="font-semibold text-slate-900">{entry.doctor_name}</h3>
                      ) : (
                        <h3 className="font-semibold text-slate-900">{entry.specialty_name || 'Qualquer especialidade'}</h3>
                      )}
                      <div className="flex flex-wrap gap-2 mt-2 text-sm text-slate-500">
                        {entry.accept_any_doctor && <Badge variant="outline">Qualquer médico</Badge>}
                        {entry.accept_any_clinic && <Badge variant="outline">Qualquer clínica</Badge>}
                        {entry.notify_if_today && <Badge variant="outline">Notificar se hoje</Badge>}
                      </div>
                      <p className="text-xs text-slate-400 mt-3">
                        Criado em {format(new Date(entry.created_at), "dd/MM/yyyy 'às' HH:mm")}
                      </p>
                    </div>
                  </div>
                  {entry.status === 'active' && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => handleDeleteEntry(entry.id)}
                      data-testid={`delete-waitlist-${entry.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
            <Clock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Lista de espera vazia</h3>
            <p className="text-slate-600 mb-6">
              Adicione uma entrada para receber alertas quando horários ficarem disponíveis
            </p>
            <Button onClick={() => setDialogOpen(true)} className="bg-primary">
              <Plus className="w-4 h-4 mr-2" />
              Adicionar à Lista
            </Button>
          </div>
        )}
      </div>

      {/* Add Entry Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar à Lista de Espera</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Especialidade</Label>
              <Select 
                value={newEntry.specialty_id} 
                onValueChange={(value) => setNewEntry({ ...newEntry, specialty_id: value })}
              >
                <SelectTrigger data-testid="waitlist-specialty">
                  <SelectValue placeholder="Selecione a especialidade" />
                </SelectTrigger>
                <SelectContent>
                  {specialties.map((specialty) => (
                    <SelectItem key={specialty.id} value={specialty.id}>
                      {specialty.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="any-doctor"
                  checked={newEntry.accept_any_doctor}
                  onCheckedChange={(checked) => setNewEntry({ ...newEntry, accept_any_doctor: checked })}
                />
                <Label htmlFor="any-doctor" className="cursor-pointer">
                  Aceito qualquer médico desta especialidade
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="any-clinic"
                  checked={newEntry.accept_any_clinic}
                  onCheckedChange={(checked) => setNewEntry({ ...newEntry, accept_any_clinic: checked })}
                />
                <Label htmlFor="any-clinic" className="cursor-pointer">
                  Aceito qualquer clínica
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="notify-today"
                  checked={newEntry.notify_if_today}
                  onCheckedChange={(checked) => setNewEntry({ ...newEntry, notify_if_today: checked })}
                />
                <Label htmlFor="notify-today" className="cursor-pointer">
                  Me avise se abrir horário hoje
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="notify-earlier"
                  checked={newEntry.notify_if_earlier}
                  onCheckedChange={(checked) => setNewEntry({ ...newEntry, notify_if_earlier: checked })}
                />
                <Label htmlFor="notify-earlier" className="cursor-pointer">
                  Me avise se abrir horário mais cedo
                </Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateEntry} className="bg-primary" data-testid="confirm-waitlist-btn">
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
